import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { NavLink } from "react-router-dom";
import cs from "classnames";

import { useCustomerContext } from "../../../lib/context/Customer/CustomerContext";
import { useDevicesRequests } from "../../../api/graphql/devices/useDevicesRequests";
import { useConfirmation } from "../../../lib/context/Confirmation/ConfirmationContext";
import { useTimeZoneContext } from "../../../lib/context/TimeZone/TimeZoneContext";
import { useMediaQuery } from "../../../lib/hooks/useMediaQuery";

import { trimText } from "../../../lib/helpers/trimText";
import { getDeviceAssignedName } from "../lib/getDeviceAssignedName";
import { WORKPLACES_ROOT_PATH } from "../../../lib/routes/routes";

import {
  StructureTable,
  StructureTableColumn,
  StructureTableRows,
} from "../../shared/StructureTable/StructureTable";
import {
  sortStructureTableData,
  SortDirection,
} from "../../shared/StructureTable/sortUtils";
import { Icon } from "../../shared/Icon/Icon";
import { DeviceFormFields } from "../../../api/graphql/workplaces";
import {
  DeviceFields,
  MAX_DEVICE_TELEMETRY_SENSORS,
} from "../../../api/graphql/devices/devices";
import { DeleteButton } from "../../shared/DeleteButton/DeleteButton";
import { EditDevice } from "../../Workplaces/EditWorkplace/EditDevice/EditDevice";
import { EmptyWorkplaces } from "../../Workplaces/EmptyWorkplaces/EmptyWorkplaces";
import { DeviceApp } from "../DeviceApp/DeviceApp";
import { SUCCESS_STRINGS } from "../../../lib/utils/constants";
import { DeviceStatus } from "../lib/DeviceStatus";
import { Tooltip } from "../../shared/Tooltip/Tooltip";
import { trimOrEmpty } from "../../../lib/helpers/normalizeString";
import {
  getConnectionSortMeta,
  getConnectionStatus,
  toSemverValue,
} from "./lib/helpers";
import { DeviceWithSortMeta } from "./lib/types";
import { supportsDateTimeSettings } from "../lib/deviceDateTime";
import { DeviceTimeConfigCell } from "./DeviceTimeConfigCell";

// device images
import Wayfinder3D from "../../../assets/images/Wayfinder-3D.webp";
import RoomDisplay3D from "../../../assets/images/RoomDevice-3D.webp";

interface Props {
  data: DeviceFields[];
  initialDeviceId?: string;
  onListChange?: () => void;
}

const toDeviceFormFields = (device: DeviceFields): DeviceFormFields => ({
  id: device.id,
  name: device.name,
  description: device.description,
  type: "existing",
  typeOfWorkspace: device.typeOfWorkspace,
  isOnline: device.isOnline,
  unassignedSince: device.unassignedSince,
  warnedUnassigned: device.warnedUnassigned,
  roomId: device.room?.id || "",
  wayFinderId: device.wayFinder?.id || "",
  deviceId: device.deviceID,
  tags: [],
  softwareInfo: device.softwareInfo,
  hardwareInfo: device.hardwareInfo,
  telemetrySensors: device.telemetrySensors?.slice(
    0,
    MAX_DEVICE_TELEMETRY_SENSORS
  ),
  autoUpdate: device.autoUpdate,
  lastDevicePing: device.lastDevicePing,
  timezone: device.timezone,
  locale: device.locale,
  autoTimeEnabled: device.autoTimeEnabled,
  format24hEnabled: device.format24hEnabled,
  deviceSupportedFeature: device.deviceSupportedFeature,
});

const columns: StructureTableColumn[] = [
  {
    key: "device_icon",
    header: "Device",
  },
  {
    key: "device_name",
    header: "Name",
    sortable: true,
    sortKey: "sortMeta.name",
  },
  {
    key: "device_connection",
    header: "Latest sync",
    sortable: true,
    sortKey: "sortMeta.connectionOrder",
  },
  {
    key: "push_connectivity",
    header: "Push connectivity",
    sortable: true,
    sortKey: "sortMeta.pushConnectivityOrder",
  },
  {
    key: "device_app",
    header: "App",
    sortable: true,
    sortKey: "sortMeta.appName",
  },
  {
    key: "device_app_version",
    header: "App version",
    sortable: true,
    sortKey: "sortMeta.versionValue",
  },
  {
    key: "device_hardware",
    header: "Hardware",
    sortable: true,
    sortKey: "sortMeta.hardware",
  },
  {
    key: "device_description",
    header: "Description",
    sortable: true,
    sortKey: "sortMeta.timeConfig",
  },
  {
    key: "device_assign",
    header: "Assigned status",
    sortable: true,
    sortKey: "sortMeta.assignOrder",
  },
  {
    key: "device_delete",
    header: "",
  },
];

// Supported rows show their time config in the description column
const timeConfigColumns = columns.map((column) =>
  column.key === "device_description"
    ? { ...column, header: "Time config" }
    : column
);

// The whole row opens the Device Modal on the table layout, so links and
// buttons inside it keep the click to themselves
const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

export const DevicesList = (props: Props) => {
  const { data, initialDeviceId, onListChange } = props;
  const smallScreen = useMediaQuery("(max-width: 1600px)");
  const { timeZone } = useTimeZoneContext();
  const confirmation = useConfirmation();
  const { customer } = useCustomerContext();
  const { disconnectDevice, unpairDevice, disconnectDeviceWayfinder } =
    useDevicesRequests();
  const [selectedDevice, setSelectedDevice] = useState<
    DeviceFormFields | undefined
  >(undefined);
  const [sortedColumnKey, setSortedColumnKey] = useState<string>("device_name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const openedInitialDeviceId = useRef<string | undefined>(undefined);

  const devicesWithSortMeta = useMemo<DeviceWithSortMeta[]>(
    () =>
      data.map((device) => {
        const connectionStatus = getConnectionStatus(device, timeZone);
        const { connectionOrder, connectionLabel } =
          getConnectionSortMeta(connectionStatus);
        const isAssigned = !!device.room || !!device.wayFinder;
        const isPushAvailable = device.isOnline || device.isOnline === null;
        const appName =
          device.typeOfWorkspace === "wayFinder"
            ? "Wayfinder X"
            : "Room Display X";

        // pushConnectivityOrder and assignOrder have 0 when Positive values because of PLN-4318
        return {
          ...device,
          sortMeta: {
            name: trimOrEmpty(device.name),
            connectionOrder,
            connectionLabel,
            pushConnectivityOrder: isPushAvailable ? 0 : 1,
            pushConnectivity: isPushAvailable ? "available" : "unavailable",
            appName,
            versionValue: toSemverValue(device.softwareInfo?.version),
            hardware: trimOrEmpty(device.hardwareInfo?.model),
            timeConfig: supportsDateTimeSettings(device)
              ? trimOrEmpty(device.timezone)
              : trimOrEmpty(device.description),
            assignOrder: isAssigned ? 0 : 1,
          },
        };
      }),
    [data, timeZone]
  );

  const sortedDevices = useMemo<DeviceWithSortMeta[]>(
    () =>
      sortStructureTableData(
        devicesWithSortMeta,
        columns,
        sortedColumnKey,
        sortDirection
      ),
    [devicesWithSortMeta, sortedColumnKey, sortDirection]
  );

  useEffect(() => {
    if (!initialDeviceId) {
      openedInitialDeviceId.current = undefined;
      return;
    }

    if (openedInitialDeviceId.current === initialDeviceId) {
      return;
    }

    const initialDevice = sortedDevices.find(
      (device) => device.id === initialDeviceId
    );

    if (!initialDevice) {
      return;
    }

    openedInitialDeviceId.current = initialDeviceId;
    setSelectedDevice(toDeviceFormFields(initialDevice));
  }, [initialDeviceId, sortedDevices]);

  const handleDeleteClick = async (
    deviceId: string,
    workspaceId: string | null,
    attachedWorkspaceType: "room" | "wayFinder"
  ) => {
    await confirmation.confirm({
      keywordDelete: "DELETE",
      title: "Are you sure you want to delete this device?",
      description:
        "By deleting this device, it will affect the rooms or wayfinders that it has been paired to!",
    });

    try {
      if (attachedWorkspaceType === "room" && workspaceId) {
        await disconnectDevice({
          id: deviceId,
          roomId: workspaceId,
          unassignedSince: Math.floor(Date.now() / 1000),
          warnedUnassigned: true,
        });

        toast.success(SUCCESS_STRINGS.deviceDisconnected);
      }

      if (attachedWorkspaceType === "wayFinder" && workspaceId) {
        await disconnectDeviceWayfinder({
          id: deviceId,
          wayFinderId: workspaceId,
          unassignedSince: Math.floor(Date.now() / 1000),
          warnedUnassigned: true,
        });

        toast.success(SUCCESS_STRINGS.deviceDisconnected);
      }

      await unpairDevice({
        id: deviceId,
        tenantId: customer.tenantId,
      });

      if (onListChange) {
        onListChange();
      }

      toast.success("Device deleted successfully!");
    } catch (error: any) {
      console.error(error.message);
      toast.error(
        error?.message ||
          "Device couldn't be deleted, please reload and try again!"
      );
    }
  };

  const rows: StructureTableRows[] = useMemo(
    () =>
      sortedDevices.map((item, index) => {
        return {
          device_icon: (
            <div className="position-relative">
              <img
                src={
                  item.typeOfWorkspace === "wayFinder"
                    ? Wayfinder3D
                    : RoomDisplay3D
                }
                alt={
                  item.typeOfWorkspace === "wayFinder"
                    ? "Wayfinder device icon"
                    : "Room display device icon"
                }
                loading="lazy"
                className="Devices__device-icon-image"
                width={48}
                height={48}
              />
              {!!item.telemetrySensors?.length && (
                <div className="Devices__device-telemetry">
                  <Icon icon="sensor" />
                  <span>Sensor</span>
                </div>
              )}
            </div>
          ),
          device_name: (
            <div
              className={cs("d-flex w-100", {
                "flex-row align-items-center justify-content-between gap-1":
                  smallScreen,
                "flex-column align-items-start": !smallScreen,
              })}
            >
              <h3
                className="link mb-0"
                onClick={(e) => {
                  stopPropagation(e);
                  handleRowClick(index);
                }}
              >
                <Tooltip content={item.name}>
                  {trimText(item.name, smallScreen ? 150 : 15)}
                </Tooltip>
              </h3>

              {smallScreen &&
                DeviceStatus({
                  lastDevicePing: item.lastDevicePing,
                  typeOfWorkspace: item.typeOfWorkspace,
                  timeZone,
                  version: item.softwareInfo?.version,
                })}
            </div>
          ),
          device_connection: smallScreen ? null : (
            <div className="Devices__device-connection-cell">
              {DeviceStatus({
                lastDevicePing: item.lastDevicePing,
                typeOfWorkspace: item.typeOfWorkspace,
                timeZone,
                version: item.softwareInfo?.version,
              })}
            </div>
          ),
          push_connectivity: (
            <div className="Devices__push-connectivity-cell">
              {item.isOnline || item.isOnline === null ? (
                <div className="d-flex flex-col Devices__push-connectivity-content">
                  <Icon icon="ring-bell-icon" />
                  <span className="text-12 text-black">Available</span>
                </div>
              ) : (
                <div className="d-flex flex-col Devices__push-connectivity-content">
                  <Icon icon="ring-bell-x-icon" />
                  <span className="text-12 text-black">Unavailable</span>
                </div>
              )}
            </div>
          ),
          device_app: (
            <DeviceApp
              typeOfWorkspace={item.typeOfWorkspace}
              name={
                item.typeOfWorkspace === "wayFinder"
                  ? "Wayfinder X"
                  : "Room Display X"
              }
            />
          ),
          device_app_version: !!item.softwareInfo ? (
            <h4 className="mb-0">
              {trimText(
                item.softwareInfo.version === "undefined"
                  ? "-"
                  : item.softwareInfo.version,
                20
              )}
            </h4>
          ) : (
            <h4 className="mb-0">-</h4>
          ),
          device_hardware: !!item.hardwareInfo ? (
            <h4 className="mb-0">
              <Tooltip content={item.hardwareInfo.model}>
                {trimText(item.hardwareInfo.model, smallScreen ? 100 : 12)}
              </Tooltip>
            </h4>
          ) : (
            <h4 className="mb-0">-</h4>
          ),
          device_description: supportsDateTimeSettings(item) ? (
            <DeviceTimeConfigCell timeZone={item.timezone} />
          ) : (
            <span>
              {!!item.description?.length ? (
                <Tooltip content={item.description}>
                  {trimText(item.description, smallScreen ? 100 : 15)}
                </Tooltip>
              ) : (
                <h4 className="mb-0">-</h4>
              )}
            </span>
          ),
          device_assign: (
            <div
              className={`Devices__${
                item.typeOfWorkspace === "wayFinder" ? "wayfinder" : "room"
              }`}
            >
              {!!item.room || !!item.wayFinder ? (
                <>
                  <h4 className="mb-0">Assigned</h4>
                  <NavLink
                    onClick={stopPropagation}
                    title={item[getDeviceAssignedName(item.room)]?.name}
                    to={`${WORKPLACES_ROOT_PATH}/${item[
                      getDeviceAssignedName(item.room)
                    ]?.__typename.toLowerCase()}/${
                      item[getDeviceAssignedName(item.room)]?.id
                    }`}
                  >
                    <Tooltip
                      content={
                        item[getDeviceAssignedName(item.room)]?.name || ""
                      }
                    >
                      {"(" +
                        trimText(
                          item[getDeviceAssignedName(item.room)]?.name || "",
                          15
                        ) +
                        ")"}
                    </Tooltip>
                  </NavLink>
                </>
              ) : (
                <h4 className="mb-0">Unassigned</h4>
              )}
            </div>
          ),
          device_delete: (
            <div className="ml-auto" onClick={stopPropagation}>
              <DeleteButton
                onClick={() =>
                  handleDeleteClick(
                    item.id,
                    item[getDeviceAssignedName(item.room)]?.id || null,
                    getDeviceAssignedName(item.room)
                  )
                }
              />
            </div>
          ),
        };
      }),
    [sortedDevices, smallScreen, timeZone]
  );

  const handleRowClick = (index: number) => {
    const deviceData = sortedDevices[index];

    setSelectedDevice(toDeviceFormFields(deviceData));
  };

  const hasTimeConfig = data.some(supportsDateTimeSettings);

  const handleSort = (columnKey: string) => {
    if (columnKey === sortedColumnKey) {
      setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }

    setSortedColumnKey(columnKey);
    setSortDirection("desc");
  };

  return (
    <>
      <StructureTable
        rows={rows}
        columns={hasTimeConfig ? timeConfigColumns : columns}
        onRowClick={smallScreen ? undefined : handleRowClick}
        noResultsPlaceholder={<EmptyWorkplaces type="Device" />}
        onSort={handleSort}
        sortedColumnKey={sortedColumnKey}
        sortDirection={sortDirection}
      />
      {selectedDevice !== undefined && (
        <EditDevice
          isOpen={selectedDevice !== undefined}
          data={selectedDevice}
          toggle={() => setSelectedDevice(undefined)}
          refetch={onListChange}
        />
      )}
    </>
  );
};
